import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useBack } from '../hooks';
import { C, cs } from '../styles/common';
import { useToast } from '../context/ToastContext';
import PutBackDialog from '../components/PutBackDialog';
import OrdersAtPoint from '../components/OrdersAtPoint';

// See the board at this point (Phase 6, slice 3, 6 Oct 2026).
//
// The board as it was just after one entry of its history, side by side with
// the board now, and what has happened to each run since -- and the orders
// the same way, under it, because reverting puts both back (the director,
// 6 Oct). One line at the top says what changed since. Looking changes
// nothing. "Put the board and orders back to this point" opens a dialog that
// says what would go back and asks first (the director, 6 Oct).

const TEAL = C.teal, AMBER = '#f59e0b', RED = '#f87171', DIM = '#9ca3af';
const DAY = 86400000;

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (s, n) => iso(new Date(new Date(s + 'T12:00:00Z').getTime() + n * DAY));
const mondayOf = (s) => { const d = new Date(s + 'T12:00:00Z'); return addDays(s, -((d.getUTCDay() + 6) % 7)); };
const dayHead = (s) => new Date(s + 'T12:00:00Z').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
// Column headings: 'Mon 21', the month only where it starts.
const colHead = (s, first) => {
  const d = new Date(s + 'T12:00:00Z');
  const wd = d.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
  const mon = d.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' });
  return `${wd} ${d.getUTCDate()}${first || d.getUTCDate() === 1 ? ' ' + mon : ''}`;
};
const daysSaid = (n) => `${n} day${n === 1 ? '' : 's'}`;
const short = (s) => new Date(s + 'T12:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const range = (a, b) => (a === b ? short(a) : `${short(a)}–${short(b)}`);
const isWeekend = (s) => { const d = new Date(s + 'T12:00:00Z').getUTCDay(); return d === 0 || d === 6; };
const hue = (id) => ((Number(id) || 0) * 47) % 360;

const WEEKS = 3;

export default function HistoryBoard() {
  const navigate = useNavigate();
  const goBack = useBack('/admin/history?type=board');
  const [params] = useSearchParams();
  const eventId = params.get('event');
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);
  const [view, setView]   = useState('then');
  const [start, setStart] = useState(null);
  const [putting, setPutting] = useState(false);
  // What reverting would do, worked out while he looks: the pop-up opens at
  // once, and a point with nothing to revert says so on the button.
  const [revert, setRevert] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    let live = true;
    setData(null); setError(null); setRevert(null);
    api.get('/api/v1/history/board-at', { params: { event: eventId } })
      .then(r => {
        if (!live) return;
        setData(r.data);
        setStart(mondayOf(String(r.data.point.at).slice(0, 10)));
      })
      .catch(e => live && setError(e.response?.data || { error: e.message }));
    api.get('/api/v1/history/put-back', { params: { event: eventId } })
      .then(r => live && setRevert(r.data))
      .catch(() => {});      // the pop-up asks again, and says why if it cannot
    return () => { live = false; };
  }, [eventId]);

  const days = useMemo(() => (start ? Array.from({ length: WEEKS * 7 }, (_, i) => addDays(start, i)) : []), [start]);

  // What each run became since, to say on the "then" board, and what it was,
  // to say on the "now" board.
  const byKey = useMemo(() => {
    const m = new Map();
    for (const c of (data && data.changes) || []) m.set(c.key, c);
    return m;
  }, [data]);

  if (error) {
    const earliest = (error.options || []).find(o => o.do === 'earliest');
    return (
      <Page goBack={goBack}>
        <div style={{ ...cs.card, maxWidth: 640 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{error.error}</div>
          {error.why && <div style={{ fontSize: 13, color: DIM, marginTop: 6, lineHeight: 1.55 }}>{error.why}</div>}
          {earliest && (
            <button onClick={() => navigate(`/admin/history/board?event=${earliest.event_id}`)} style={{ ...btn, marginTop: 12, color: TEAL, borderColor: TEAL }}>
              {earliest.label} <i className="fa-solid fa-arrow-right" style={{ fontSize: 10, marginLeft: 4 }} />
            </button>
          )}
        </div>
      </Page>
    );
  }
  if (!data || !start) return <Page goBack={goBack}><div style={{ color: C.muted, fontSize: 13 }}>Rebuilding the board…</div></Page>;

  const rows = view === 'then' ? data.then : data.now;
  const shown = rows.filter(r => r.scheduled_date >= days[0] && r.scheduled_date <= days[days.length - 1]);
  const cells = new Map();
  for (const r of shown) {
    const k = r.machine_id + '|' + r.scheduled_date;
    if (!cells.has(k)) cells.set(k, []);
    cells.get(k).push(r);
  }
  const counts = { moved: 0, added: 0, removed: 0, quantity: 0 };
  for (const c of data.changes) counts[c.kind] = (counts[c.kind] || 0) + 1;
  const machName = (id) => (data.machines.find(m => m.id === id) || {}).name || `#${id}`;
  const partName = (id) => data.parts[id] || `Part ${id}`;
  // Which order an order line belongs to, then or now.
  const orderOfLine = new Map();
  for (const o of data.orders || []) {
    for (const side of [o.then, o.now]) for (const l of (side && side.lines) || []) orderOfLine.set(l.id, { id: o.id, number: o.number });
  }
  const ordersOf = (lineIds) => {
    const seen = new Map();
    for (const l of lineIds || []) { const o = orderOfLine.get(l); if (o) seen.set(o.id, o); }
    return [...seen.values()];
  };
  const goOrder = (id) => { const el = document.getElementById(`order-${id}`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  function tip(r) {
    const c = byKey.get(r.plan_id || 'row-' + r.id);
    const forOrder = ordersOf([r.order_line_id, ...(Array.isArray(r.order_line_ids) ? r.order_line_ids : [])]).map(o => o.number).join(', ');
    const base = `${partName(r.part_id)} · ${machName(r.machine_id)} · ${dayHead(r.scheduled_date)} · ${(r.target_qty || 0).toLocaleString('en-GB')} pcs`
      + (forOrder ? `\nFor ${forOrder}` : '');
    if (r.change === 'removed') return `${base}\nTaken off the board since.`;
    if (r.change === 'added') return `${base}\nPut on the board since.`;
    if (c && c.kind === 'moved' && c.then && c.now) {
      return view === 'then'
        ? `${base}\nSince moved: now ${machName(c.now.machine_id)} ${range(c.now.from, c.now.to)}.`
        : `${base}\nThen: ${machName(c.then.machine_id)} ${range(c.then.from, c.then.to)}.`;
    }
    if (r.change === 'quantity' && c) return `${base}\nQuantity changed since (${c.then.qty.toLocaleString('en-GB')} → ${c.now.qty.toLocaleString('en-GB')}).`;
    return base;
  }

  return (
    <Page goBack={goBack}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>
            {view === 'then' ? 'The board and orders as they were' : 'The board and orders as they are now'}
          </h1>
          <div style={{ fontSize: 13.5, color: DIM, marginTop: 6, lineHeight: 1.5 }}>
            <b style={{ color: '#e5e7eb' }}>{data.point.said}</b>, just after: <i>“{data.point.summary}”</i>{data.point.actor ? ` (${data.point.actor})` : ''}
          </div>
          <SinceLine changes={data.changes} orders={data.orders || []} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: TEAL,
                         background: C.tealDim, border: `1px solid ${TEAL}55`, borderRadius: 6, padding: '4px 10px' }}>
            <i className="fa-solid fa-eye" style={{ marginRight: 6 }} />Viewing only
          </span>
          {revert && revert.nothing ? (
            <button disabled title="The board and the orders are exactly as they were at this point."
              style={{ ...btn, color: C.muted, cursor: 'not-allowed', opacity: 0.6 }}>
              <i className="fa-solid fa-clock-rotate-left" style={{ marginRight: 6 }} />Nothing to revert
            </button>
          ) : (
            <button onClick={() => setPutting(true)} style={{ ...btn, color: AMBER, borderColor: `${AMBER}88`, fontWeight: 600 }}>
              <i className="fa-solid fa-clock-rotate-left" style={{ marginRight: 6 }} />Revert back to this point
            </button>
          )}
        </div>
      </div>
      {putting && (
        <PutBackDialog eventId={eventId} initial={revert} onClose={() => setPutting(false)}
          onDone={(r) => {
            setPutting(false);
            toast.success(r.note ? `${r.summary}. ${r.note}` : r.summary, 9000);
            navigate('/admin/schedule');
          }} />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ display: 'inline-flex', border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
          {[['then', 'As it was'], ['now', 'As it is now']].map(([k, label], i) => (
            <button key={k} onClick={() => setView(k)} aria-pressed={view === k}
              style={{ background: view === k ? C.tealDim : 'none', color: view === k ? TEAL : DIM, border: 'none',
                       borderRight: i === 0 ? `1px solid ${C.border}` : 'none', padding: '6px 14px', fontSize: 13,
                       fontWeight: view === k ? 700 : 500, cursor: 'pointer' }}>
              {label}
            </button>
          ))}
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <button onClick={() => setStart(addDays(start, -7))} style={btn} aria-label="A week earlier"><i className="fa-solid fa-chevron-left" /></button>
          <span style={{ fontSize: 13, color: DIM, minWidth: 150, textAlign: 'center' }}>{range(days[0], days[days.length - 1])}</span>
          <button onClick={() => setStart(addDays(start, 7))} style={btn} aria-label="A week later"><i className="fa-solid fa-chevron-right" /></button>
          <button onClick={() => setStart(mondayOf(String(data.point.at).slice(0, 10)))} style={btn}>Week of this point</button>
        </div>
      </div>

      <Legend view={view} />

      <div style={{ ...cs.tableWrap, marginBottom: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: `140px ${days.map(d => (isWeekend(d) ? '34px' : 'minmax(78px, 1fr)')).join(' ')}`,
                      minWidth: 140 + days.length * 60 }}>
          <div style={{ ...head, position: 'sticky', left: 0, zIndex: 2 }}>Machine</div>
          {days.map(d => (
            <div key={d} style={{ ...head, textAlign: 'center', opacity: isWeekend(d) ? 0.45 : 1,
                                  borderLeft: new Date(d + 'T12:00:00Z').getUTCDay() === 1 ? `1px solid ${C.border}` : 'none' }}>
              {isWeekend(d) ? dayHead(d).slice(0, 2) : colHead(d, d === days[0])}
            </div>
          ))}
          {data.machines.slice().sort((a, b) => a.name.localeCompare(b.name)).map(m => (
            <React.Fragment key={m.id}>
              <div style={{ padding: '8px 10px', borderTop: `1px solid ${C.border}`, fontSize: 12.5, fontWeight: 600,
                            position: 'sticky', left: 0, background: C.surface, zIndex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {m.name}
              </div>
              {days.map(d => (
                <div key={d} style={{ borderTop: `1px solid ${C.border}`, padding: 3, minHeight: 34,
                                      background: isWeekend(d) ? 'rgba(255,255,255,0.015)' : 'transparent',
                                      borderLeft: new Date(d + 'T12:00:00Z').getUTCDay() === 1 ? `1px solid ${C.border}` : 'none',
                                      display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {(cells.get(m.id + '|' + d) || []).map(r => <RunChip key={r.id} r={r} label={partName(r.part_id)} tip={tip(r)} />)}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
      {shown.length === 0 && (
        <div style={{ fontSize: 13, color: C.muted, marginTop: -8, marginBottom: 16 }}>Nothing on the board in these weeks — use the arrows to look earlier or later.</div>
      )}

      <div id="board-changes" style={{ ...st.eyebrow }}>What happened to the board since then</div>
      {data.changes.length === 0 ? (
        <div style={{ fontSize: 13, color: C.muted }}>Nothing: the board is exactly as it was.</div>
      ) : (
        <div style={{ ...cs.tableWrap }}>
          {[['moved', 'Moved since', AMBER], ['added', 'Put on the board since', TEAL], ['removed', 'Taken off the board since', RED], ['quantity', 'Quantity changed since', DIM]]
            .filter(([k]) => counts[k]).map(([k, title, tone]) => (
              <div key={k}>
                <div style={{ padding: '8px 14px', background: C.surfaceAlt, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                              textTransform: 'uppercase', color: tone, borderTop: `1px solid ${C.border}` }}>
                  {title} · {counts[k]}
                </div>
                {data.changes.filter(c => c.kind === k).map(c => {
                  const at = c.then || c.now;
                  return (
                    <div key={c.key} onClick={() => setStart(mondayOf(at.from))} title="Show these weeks on the board above"
                      style={{ padding: '8px 14px', borderTop: `1px solid ${C.border}`, fontSize: 13, cursor: 'pointer',
                               display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
                      <b style={{ minWidth: 220 }}>{partName(c.part_id)}</b>
                      {ordersOf(c.lines).map(o => (
                        <button key={o.id} onClick={e => { e.stopPropagation(); goOrder(o.id); }} title="Show this order below"
                          style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 5, padding: '1px 8px', cursor: 'pointer',
                                   color: '#e5e7eb', fontSize: 12, fontWeight: 600 }}>
                          <i className="fa-solid fa-box" style={{ fontSize: 9, marginRight: 5, color: DIM }} />{o.number}
                        </button>
                      ))}
                      {!ordersOf(c.lines).length && <span style={{ color: C.mutedDark, fontSize: 12 }}>no order</span>}
                      {c.then && <span style={{ color: DIM }}>then {machName(c.then.machine_id)} {range(c.then.from, c.then.to)}{c.now && c.now.days !== c.then.days ? ` (${daysSaid(c.then.days)})` : ''}{k === 'quantity' ? ` · ${c.then.qty.toLocaleString('en-GB')} pcs` : ''}</span>}
                      {c.then && c.now && <span style={{ color: tone }}>→</span>}
                      {c.now && <span style={{ color: '#e5e7eb' }}>now {machName(c.now.machine_id)} {range(c.now.from, c.now.to)}{c.then && c.now.days !== c.then.days ? ` (${daysSaid(c.now.days)})` : ''}{k === 'quantity' ? ` · ${c.now.qty.toLocaleString('en-GB')} pcs` : ''}</span>}
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      )}

      <OrdersAtPoint orders={data.orders || []} parts={data.parts} view={view} changes={data.changes} mach={machName} />
    </Page>
  );
}

// What changed since the point, in one line: the decision at a glance. Each
// part jumps to its detail below.
function SinceLine({ changes, orders }) {
  const runs = { moved: 0, added: 0, removed: 0, quantity: 0 };
  for (const c of changes) runs[c.kind] = (runs[c.kind] || 0) + 1;
  const ord = {
    changed: orders.filter(o => o.kind === 'changed' || o.kind === 'lines_unknown').length,
    taken: orders.filter(o => o.kind === 'taken_since').length,
    deleted: orders.filter(o => o.kind === 'deleted_since').length,
  };
  const n = (k, one, many = one + 's') => `${k} ${k === 1 ? one : many}`;
  const parts = [
    runs.moved && [n(runs.moved, 'run') + ' moved', 'board-changes', AMBER],
    runs.added && [n(runs.added, 'run') + ' put on the board', 'board-changes', TEAL],
    runs.removed && [n(runs.removed, 'run') + ' taken off', 'board-changes', RED],
    runs.quantity && [n(runs.quantity, 'quantity', 'quantities') + ' changed', 'board-changes', DIM],
    ord.changed && [n(ord.changed, 'order') + ' changed', 'orders-at-point', AMBER],
    ord.taken && [n(ord.taken, 'order') + ' taken', 'orders-at-point', TEAL],
    ord.deleted && [n(ord.deleted, 'order') + ' deleted', 'orders-at-point', RED],
  ].filter(Boolean);
  const go = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  return (
    <div style={{ fontSize: 13.5, marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '4px 0', alignItems: 'baseline' }}>
      <span style={{ color: DIM, marginRight: 6 }}>Since then:</span>
      {parts.length === 0 && <span style={{ color: '#e5e7eb' }}>nothing has changed.</span>}
      {parts.map(([text, id, tone], i) => (
        <span key={text}>
          {i > 0 && <span style={{ color: C.mutedDark, margin: '0 7px' }}>·</span>}
          <button onClick={() => go(id)} title="Show me"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: tone, fontSize: 13.5, fontWeight: 600,
                     textDecoration: 'underline', textDecorationColor: `${tone}55`, textUnderlineOffset: 3 }}>
            {text}
          </button>
        </span>
      ))}
    </div>
  );
}

function RunChip({ r, label, tip }) {
  const h = hue(r.part_id);
  const outline = r.change === 'moved' ? `2px solid ${AMBER}`
    : r.change === 'added' ? `2px solid ${TEAL}`
    : r.change === 'removed' ? `2px dashed ${RED}`
    : r.change === 'quantity' ? `2px dotted ${DIM}` : `1px solid hsl(${h} 40% 40%)`;
  return (
    <div title={tip}
      style={{ background: `hsl(${h} 35% 24%)`, border: outline, borderRadius: 4, padding: '2px 5px', fontSize: 10.5,
               color: '#e5e7eb', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', opacity: r.change === 'removed' ? 0.7 : 1 }}>
      {r.change === 'added' && <span style={{ color: TEAL, fontWeight: 700, marginRight: 4 }}>NEW</span>}
      {label}
    </div>
  );
}

function Legend({ view }) {
  const item = (style, text) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 16, height: 11, borderRadius: 3, background: '#2a3340', ...style }} />{text}
    </span>
  );
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: DIM, marginBottom: 10 }}>
      {item({ border: `2px solid ${AMBER}` }, view === 'then' ? 'moved since' : 'moved since then')}
      {view === 'then'
        ? item({ border: `2px dashed ${RED}` }, 'taken off the board since')
        : item({ border: `2px solid ${TEAL}` }, 'put on the board since')}
      {item({ border: `2px dotted ${DIM}` }, 'quantity changed since')}
      <span style={{ color: C.muted }}>Hover a run for what happened to it.</span>
    </div>
  );
}

function Page({ children, goBack }) {
  const navigate = useNavigate();
  return (
    <div style={{ ...cs.page, padding: 'clamp(16px, 3vw, 24px)', paddingBottom: 84, boxSizing: 'border-box' }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 12 }}>
          <button onClick={goBack} style={st.crumbBtn}><i className="fa-solid fa-chevron-left" style={{ fontSize: 10 }} /> Back</button>
          <span style={{ color: C.mutedDark }}>·</span>
          <button onClick={() => navigate('/admin/history')} style={st.crumbBtn}>History</button>
          <span style={{ color: C.mutedDark }}>·</span>
          <span style={{ color: DIM }}>The board at a point</span>
        </div>
        {children}
      </div>
    </div>
  );
}

const btn = { padding: '6px 11px', background: 'transparent', color: DIM, border: `1px solid ${C.border}`, borderRadius: 7, cursor: 'pointer', fontSize: 12.5, whiteSpace: 'nowrap' };
const head = { padding: '8px 6px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: C.muted, background: C.surfaceAlt, whiteSpace: 'nowrap' };
const st = {
  crumbBtn: { background: 'none', border: 'none', color: C.muted, fontSize: 12, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 },
  eyebrow:  { fontSize: 10, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: 9 },
};
